import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Stable key for scroll-to-error. Rendered as `data-field` on the wrapper. */
  fieldKey?: string;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ label, error, hint, required, className, id, fieldKey, ...rest }, ref) => {
    const { t } = useTranslation();
    const inputId = id || `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const errorMessage = error ? t(`validation.${error}`, { defaultValue: error }) : undefined;

    return (
      <div
        className="flex w-full scroll-mt-24 flex-col gap-1.5"
        data-field={fieldKey}
      >
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-haat-red-600 ms-1">*</span>}
        </label>
        <input
          id={inputId}
          ref={ref}
          aria-invalid={!!error}
          className={cn('input-modern', error && 'has-error', className)}
          {...rest}
        />
        {errorMessage ? (
          <span className="text-sm font-medium text-red-600">{errorMessage}</span>
        ) : hint ? (
          <span className="text-sm text-gray-500">{hint}</span>
        ) : null}
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';

export default FormInput;
